import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  CircularProgress,
  ListSubheader,
  Avatar,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AllInclusive as AllIcon,
} from '@mui/icons-material';
import { useLocations } from '../hooks/useAnalytics';

interface BrandSelectorProps {
  value: string | string[];
  onChange: (brandId: string | string[]) => void;
  multiple?: boolean;
}

const brandColors: Record<string, string> = {
  'Savvy Sliders': '#af2326',
  'Happy\'s Pizza': '#ff6b35',
  'BurgerFi': '#1976d2',
};

const brandIcons: Record<string, string> = {
  'Savvy Sliders': '🍔',
  'Happy\'s Pizza': '🍕',
  'BurgerFi': '🍔',
};

const BrandSelector: React.FC<BrandSelectorProps> = ({ value, onChange, multiple = false }) => {
  const { data, isLoading, error } = useLocations();

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    onChange(event.target.value);
  };

  if (error) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel>Error loading brands</InputLabel>
        <Select value="" />
      </FormControl>
    );
  }

  const selectedBrands = multiple ? (value as string[]) : [value as string];
  const isAllSelected = !multiple && value === 'all';

  return (
    <FormControl fullWidth>
      <InputLabel id="brand-selector-label">
        {isLoading ? 'Loading brands...' : 'Select Brand'}
      </InputLabel>
      <Select
        labelId="brand-selector-label"
        value={value}
        onChange={handleChange}
        label="Select Brand"
        multiple={multiple}
        renderValue={(selected) => {
          if (!multiple && selected === 'all') {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AllIcon sx={{ fontSize: 20 }} />
                <span>All Brands</span>
              </Box>
            );
          }
          
          const selectedArray = multiple ? selected as string[] : [selected as string];
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedArray.map((brand) => (
                <Chip
                  key={brand}
                  label={brand}
                  size="small"
                  sx={{
                    backgroundColor: brandColors[brand] || '#666',
                    color: 'white',
                  }}
                  avatar={<Avatar sx={{ backgroundColor: 'transparent !important' }}>{brandIcons[brand]}</Avatar>}
                />
              ))}
            </Box>
          );
        }}
        startAdornment={
          isLoading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
          )
        }
      >
        {!multiple && (
          <MenuItem value="all">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AllIcon />
              <span>All Brands</span>
              {data?.brands && (
                <Chip
                  label={`${data.brands.length} brands`}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>
          </MenuItem>
        )}

        {data?.brands?.map((brand: string) => (
          <MenuItem key={brand} value={brand}>
            {multiple && (
              <Checkbox checked={selectedBrands.includes(brand)} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: brandColors[brand] || '#666',
                  fontSize: '1rem',
                }}
              >
                {brandIcons[brand]}
              </Avatar>
              <ListItemText primary={brand} />
              {data.byBrand && data.byBrand[brand] && (
                <Chip
                  label={`${data.byBrand[brand].length} stores`}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default BrandSelector;